import {useEffect, useState} from "react";
import api from "@/api.js";
import Auth from "@/Auth.js";
import FileDownload from "js-file-download";
import {Modal, Collapse, Table, Upload, Button} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import styles from "./AddImageModal.module.css";
import {App as AntdApp} from "antd";

export const AddImageModal = ({show, onClose, item}) => {
	const {message, modal} = AntdApp.useApp();

	const [progress, setProgress] = useState("");
	const [activeKey, setActiveKey] = useState("1");
	const [old, setOld] = useState([]);
	const [order, setOrder] = useState([]);
	const [actives, setActives] = useState([]);

	const navigate = useNavigate();

	function setAsActive(i) {
		const id = order[i];

		if (!id) {
			console.error("ID is not found:", i);
			return;
		}

		const data = {
			_id: id,
		};

		api.post(`/image/active`, data, Auth.headers())
			.then(response => onClose())
			.catch(() => {});
	}

	function deleteImage(i) {
		const id = order[i];

		if (!id) {
			console.error("ID is not found:", i);
			return;
		}

		const data = {
			_id: id,
		};

		return api.post(`/image/delete`, data, Auth.headers())
			.then(response => {
				message.success("The image was successfully deleted");
				get_ids();
			})
			.catch(() => {});
	}

	function confirmDeleteImage(i) {
		modal.confirm({
			title: "Delete image",
			content: "Are you sure you want to delete this image? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => deleteImage(i),
		});
	}

	function getImgById(id) {
		const config = Auth.headers();
		config["responseType"] = "blob";

		const data = {
			_id: id,
		};

		return api.post(`/image/id`, data, config).then(response => {
			const matrixBlob = new Blob([response.data], {type: "image/jpg"});
			const header = response.headers["content-disposition"];

			const filenameMatch = /filename=(.*)/.exec(header);
			const filename = filenameMatch ? filenameMatch[1].replace(/"/g, "") : "";

			 return new Promise(resolve => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(matrixBlob);
          fileReader.onload = () => {
              const result = fileReader.result;
              resolve({
                  id,
                  image:
                      typeof result === "string" ? (
                          <img style={{maxHeight: 150}} alt={"img-" + id} src={result} />
                      ) : null,
                  active: filename === "True",
              });
          };
      });
  })
  .catch(() => {})
         }

	function get_ids() {
		const data = {
			material: item.material,
			type: "img",
			operator: item.operator,
			part_number: item.part_number,
		};

		api.post(`/images`, data, Auth.headers())
			.then(response => {
				const ids = Object.values(response.data || {});

				return Promise.all(ids.map(getImgById));
			})
			.then(items => {
				setOld(items.map(i => i.image));
				setOrder(items.map(i => i.id));
				setActives(items.map(i => (i.active ? "active" : "")));
			})
			.catch(() => {});
	}

	function downloadImage(i) {
		const config = Auth.headers();
		config["responseType"] = "blob";

		const id = order[i];

		if (!id) {
			console.error("ID is not found:", i);
			return;
		}

		const data = {
			_id: id,
		};

		api.post(`/image/id`, data, config)
			.then(response => {
				const filename = `${item.operator}-${item.material}-${item.part_number}.jpg`;
				FileDownload(response.data, filename, filename);
			})
			.catch(() => {});
	}

	function handleSelect(activeKey) {
		setActiveKey(activeKey);
	}

	function uploadFile(e) {
		const formData = new FormData();
		const input = e.target;
		if (input.files.length > 0) {
			for (const file of input.files) {
				formData.append(file.name, file);
			}
			const config = Auth.headers();
			// config["Content-Type"] = "multipart/form-data";
			config["onUploadProgress"] = progressEvent => {
				const message =
					progressEvent.loaded < progressEvent.total ? "Uploading files..." : "Processing" + " files...";
				setProgress(message);
			};
			api.post(
				`/upload/image?operator=${item.operator}&material=${item.material}&part_number=${item.part_number}`,
				formData,
				{ ...config, omitGlobalError: true },
			)
				.then(resp => {
					if (resp.data.message) {
						message.info(resp.data.message);
					} else {
						message.success("Your files were successfully uploaded!");
					}
					onClose();
				})
				.catch(error => {
					message.error(error.response?.data?.message);
				})
				.finally(() => {
					input.value = "";
					setProgress("");
				});
		}
	}

	function handleUploadChange({file, fileList}) {
		const rawFile = fileList.find(item => item.uid === file.uid)?.originFileObj;

		if (rawFile) {
			uploadFile({target: {files: [rawFile]}});
		}
	}

	useEffect(() => {
		get_ids();
	}, []);

	const columns = [
		{
			title: "Image",
			dataIndex: "name",
			key: "image",
		},
		{
			title: "Action",
			key: "action",
			render: (_, record, index) => (
				<div className={styles.buttonContainer}>
					<Button type="link" onClick={() => setAsActive(index)}>
						Set as active
					</Button>
					<Button type="link" onClick={() => downloadImage(index)}>
						Download
					</Button>
					<Button type="link" onClick={() => confirmDeleteImage(index)}>
						Delete
					</Button>
				</div>
			),
		},
	];

	const dataSource = old.map((v, i) => ({
		key: i,
		name: v,
		status: actives[i],
	}));

	return (
		<Modal title="Image Editor" open={show} onCancel={onClose} footer={null} width={700}>
			<Collapse
				accordion
				activeKey={activeKey}
				id="accordion-controlled-example"
				onChange={handleSelect}
				items={[
					{
						key: "1",
						label: "Upload image by file",
						children: (
							<Upload
								beforeUpload={() => false}
								multiple
								onChange={handleUploadChange}
								disabled={progress !== ""}
								showUploadList={false}>
								<Button icon={<UploadOutlined />}>Click to Upload</Button>
							</Upload>
						),
					},
					...(dataSource.length > 0
						? [
								{
									key: "2",
									label: "Select previously uploaded image",
									children: (
										<Table
											dataSource={dataSource}
											columns={columns}
											pagination={false}
											size="small"
											bordered
											rowClassName={record =>
												record.status === "active" ? "ant-table-row-selected" : ""
											}
											onRow={record => ({
												style: {backgroundColor: record.status === "active" ? "#ccffcc" : ""},
											})}
										/>
									),
								},
							]
						: []),
				]}
			/>
		</Modal>
	);
};
